/**
 * @module Pages/AdminPages/AdminComponents/RevenueTrendsChart
 * @description This module contains the RevenueTrendsChart component which displays a line chart
 * for revenue trends based on various time ranges such as today, yesterday, last 7 days, last 30 days, 
 * or custom date ranges. The data is fetched from the server based on the selected time range.
 */
import { Box, Card, FormControl, FormHelperText, Typography, useTheme } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Chart } from 'chart.js';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { ContainedButton1 } from 'components/DesignSystem/Button';
import CustomDatePicker from 'components/DesignSystem/CustomDatePicker';
import { Dropdown } from 'components/DesignSystem/DropDown';
import { matchDataWithDateRange } from 'utils/filterDate';
import { fDate } from 'utils/formatTime';
/**
 * Time range options for the revenue trends chart.
 */
const timeRangeOptions = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 Days', value: 'last7days' },
  { label: 'Last 30 Days', value: 'last30days' },
  { label: 'This Month', value: 'thismonth' },
  { label: 'Custom Range', value: 'custom' },
];
/**
 * The RevenueTrendsChart component allows users to view revenue trends over various time periods. 
 * It displays a line chart with revenue data for different time ranges and supports custom date ranges.
 * 
 * @component
 * @returns {JSX.Element} A chart displaying revenue trends based on the selected time range.
 */
function RevenueTrendsCart() {
  const [timeRange, setTimeRange] = useState('today');
  const [chart, setChart] = useState(null);
  const theme = useTheme();
  /**
   * Formik hook to handle form state and validation for custom date range.
   * 
   * @type {Object}
   */
  const formik = useFormik({
    initialValues: {
      fromDate: '',
      toDate: '',
    },
    validationSchema: Yup.object().shape({
      fromDate: Yup.date()
        .typeError('Please enter valid date dd-mm-yyyy format')
        .min(dayjs('1900-01-01'), 'Date must be greater than 1900-01-01')
        .max(dayjs().endOf('day'), 'Date must be equal or before today'),
      toDate: Yup.date()
        .typeError('Please enter valid date dd-mm-yyyy format')
        .min(dayjs('1900-01-01'), 'Date must be greater than 1900-01-01')
        .max(dayjs().endOf('day'), 'Date must be equal or before today')
        .test({
          name: 'notBeforeAsFromDate',
          message: 'To Date must be equal or greater than from date',
          test: (toDate, values) => {
            const fromDate = values.parent.fromDate;
            return toDate !== null ? dayjs(fromDate).isSameOrBefore(dayjs(toDate), 'day') : false;
          },
        }),
    }),
    onSubmit: (values) => {
      fetchData(values);
    },
  });
  /**
   * Handles the change of the selected time range for revenue trends.
   * 
   * @param {Object} event - The change event triggered when a new time range is selected.
   */
  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
    formik.setValues({
      fromDate: '',
      toDate: '',
    });
  };
  /**
   * Fetches revenue data based on the selected time range and custom date range.
   * 
   * @param {Object} dateValues - Contains 'fromDate' and 'toDate' for the custom date range.
   */
  const fetchData = (dateValues) => {
    const startDate = fDate(dateValues?.fromDate?.$d, 'yyyy-MM-dd');
    const endDate = fDate(dateValues?.toDate?.$d, 'yyyy-MM-dd');

    let url = `/admin/revenue-trends?filter=${timeRange}`;
    if (timeRange === 'custom') {
      url += `&startDate=${startDate}&endDate=${endDate}`;
    }
    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        const updatedData = matchDataWithDateRange(startDate, endDate, data, timeRange, 'totalRevenue'); // totalRevenue is a key accessor for the counts.
        const labels = updatedData.map((item) => item.date); // replace dummyData with data
        const counts = updatedData.map((item) => item.totalRevenue);

        const ctx = document.getElementById('revenue-trend-chart').getContext('2d');

        if (chart) {
          chart.destroy(); // Destroy the previous chart instance
        }

        const newChart = new Chart(ctx, {
          type: 'line',
          data: {
            labels,
            datasets: [
              {
                label: 'Revenue',
                data: counts,
                fill: false,
                backgroundColor: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                tension: 0.1,
              },
            ],
          },
          options: {
            aspectRatio: 3.18, // change aspect ratio to change the map dimentions
            scales: {
              y: {
                // max: 5,
                min: 0,
                ticks: {
                  stepSize: 1,
                },
              },
            },
            plugins: {
              legend: {
                position: 'top', // Change this to 'top', 'left', 'bottom', 'right', or 'chartArea'
                align: 'end', // Aligns the legend to the end (right for 'top')
              },
            },
          },
        });

        setChart(newChart);
      })
      .catch((error) => console.error('Error fetching data:', error));
  };

  useEffect(() => {
    fetchData();
  }, []);
  /**
   * Triggers form submission to fetch data when "Apply" button is clicked.
   */
  const handleFilterClick = () => {
    formik.handleSubmit();
  };
  /**
   * Handles date change events for the custom date range inputs (fromDate and toDate).
   * 
   * @param {Date} value - The selected date value.
   * @param {string} name - The name of the field (either 'fromDate' or 'toDate').
   */
  const handleDateChange = (value, name) => {
    const event = {
      target: {
        name,
        value: value !== null ? dayjs(value) : null,
      },
    };
    formik.handleChange(event);
  };

  return (
    <Card sx={{ marginTop: '30px', boxShadow: 'none' }}>
      <Typography variant="title2" marginBottom="8px">
        Revenue Trends
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          margin: '16px 0',
          width: '100%',
          justifyContent: 'flex-start',
          alignItems: 'center',
          alignContent: 'center',
        }}
      >
        <Dropdown value={timeRange} handleChange={handleTimeRangeChange} placeholder="Time Range" options={timeRangeOptions} dropdownheight='100%' dropdownWidth='279px' />
        {timeRange === 'custom' && (
          <Box sx={{ display: 'flex', gap: '16px', width: '100%', }}>
            <FormControl required error={formik.errors.fromDate} fullWidth variant="standard">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <CustomDatePicker
                  // label="From Date"
                  value={formik.values.fromDate}
                  name="From Date"
                  error={formik.errors.fromDate}
                  fullWidth
                  inputFormat={'dd-MM-yyyy'}
                  maxDate={dayjs()}
                  onChange={(value) => handleDateChange(value, 'fromDate')}
                  onBlur={formik.handleBlur}
                  placeholder='From Date(DD-MM-YYYY)'
                />
                {formik.touched.fromDate && formik.errors.fromDate && (
                  <FormHelperText error id={`standard-weight-helper-text-fromDate`}>
                    {formik.errors.fromDate}
                  </FormHelperText>
                )}
              </LocalizationProvider>
            </FormControl>
            <FormControl required error={formik.errors.toDate} fullWidth variant="standard">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <CustomDatePicker
                  // label="To Date"
                  value={formik.values.toDate}
                  name="toDate"
                  error={formik.errors.toDate}
                  fullWidth
                  inputFormat={'dd-MM-yyyy'}
                  maxDate={dayjs()}
                  onChange={(value) => handleDateChange(value, 'toDate')}
                  onBlur={formik.handleBlur}
                  placeholder='To Date(DD-MM-YYYY)'
                />
                {formik.touched.toDate && formik.errors.toDate && (
                  <FormHelperText error id={`standard-weight-helper-text-toDate`}>
                    {formik.errors.toDate}
                  </FormHelperText>
                )}
              </LocalizationProvider>
            </FormControl>
          </Box>
        )}
        <ContainedButton1 sx={{ height: '40px', width: '10vw' }} onClick={handleFilterClick}>
          <Typography variant='bodyText3' color={theme.palette.common.white}>
            Apply
          </Typography>
        </ContainedButton1>
      </Box>
      <Box sx={{
        textAlign: 'center',
        padding: '20px',
        paddingTop: '44px',
        backgroundColor: theme.palette.grey[200],
        borderRadius: '15px'
      }}>
        <canvas id="revenue-trend-chart" />
      </Box>
    </Card>
  );
}

export default RevenueTrendsCart;
